import { useFormContext } from 'react-hook-form';
import { SettlementRuleAction, SettlementRuleType } from '@mews-commander/data-access';
import { FormInputField, FormPercentageInputField, FormRadioGroupField, LocalizedEnum, lt } from '@mews/framework';
import { CompoundField, RadioButton, Stack, StackItem } from '@optimus-web/core';
import { NumberInputField, SelectField } from '@mews-commander/form';
import { createOption } from '@mews-commander/ui';
import { PaymentPolicyTimingFields } from '../../../../settlementRules/components/PaymentPolicyTimingFields';
import { FormValues } from './schema';
import { CentralPaymentPolicyFormField } from './types';

interface CentralPaymentPolicyFormContentProps {
    disableAmountType?: boolean;
}

export const CentralPaymentPolicyFormContent = ({ disableAmountType }: CentralPaymentPolicyFormContentProps) => {
    const { clearErrors, setValue, watch } = useFormContext<FormValues>();
    const amountType = watch('amount.type');

    return (
        <Stack vertical spacing="300">
            <FormInputField autoFocus name={CentralPaymentPolicyFormField.Name} label={lt('Name')} />
            <FormInputField
                name={CentralPaymentPolicyFormField.PaymentPolicyKey}
                label={lt('PaymentPolicyKeyLabel')}
                helper={lt('PaymentPolicyKeyHelper')}
                required
            />
            <CompoundField name={CentralPaymentPolicyFormField.Amount} label={lt('Amount')} required>
                <StackItem maxWidth="33%">
                    <SelectField
                        required
                        name={`${CentralPaymentPolicyFormField.Amount}.type`}
                        options={[
                            createOption({
                                label: lt('GeneralFixedAmount'),
                                value: 'flat',
                            }),
                            createOption({
                                label: lt('SettlementPercentageValueLabel'),
                                value: 'percentage',
                            }),
                        ]}
                        onChange={(_, value) => {
                            setValue(CentralPaymentPolicyFormField.Amount, {
                                type: value as 'flat' | 'percentage',
                                value: 1,
                            });
                        }}
                        showEmptyValue={false}
                        disabled={disableAmountType}
                    />
                </StackItem>
                <StackItem grow>
                    {amountType === 'flat' ? (
                        <NumberInputField
                            name={`${CentralPaymentPolicyFormField.Amount}.value`}
                            type="number"
                            shouldUnregister
                        />
                    ) : null}
                    {amountType === 'percentage' ? (
                        <FormPercentageInputField
                            name={`${CentralPaymentPolicyFormField.Amount}.value`}
                            shouldUnregister
                        />
                    ) : null}
                </StackItem>
            </CompoundField>
            <PaymentPolicyTimingFields />
            <FormRadioGroupField
                name={CentralPaymentPolicyFormField.Action}
                label={lt('Method')}
                onChange={() => clearErrors(CentralPaymentPolicyFormField.Amount)}
                required
            >
                {Object.values(SettlementRuleAction).map((action) => (
                    <RadioButton
                        label={<LocalizedEnum prefix="SettlementRuleAction" value={action} />}
                        value={action}
                        key={action}
                    />
                ))}
            </FormRadioGroupField>
            <FormRadioGroupField name={CentralPaymentPolicyFormField.Type} label={lt('PaymentPolicyMode')} required>
                {Object.values(SettlementRuleType).map((type) => (
                    <RadioButton
                        label={<LocalizedEnum prefix="SettlementRuleType" value={type} />}
                        value={type}
                        key={type}
                    />
                ))}
            </FormRadioGroupField>
        </Stack>
    );
};
